<template>
  <div class="relative">
    <!-- Column selector button - Solo mostrar en modo administrador -->
    <div v-if="showActions" class="flex justify-end mb-4">
      <div class="relative column-selector">
        <button
          @click.stop="showColumnSelector = !showColumnSelector"
          class="px-3 py-1.5 text-sm bg-white border border-slate-200 rounded-lg hover:bg-slate-50 flex items-center gap-2"
        >
          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Columnas
        </button>

        <!-- Column selector dropdown -->
        <div
          v-if="showColumnSelector"
          class="absolute right-0 top-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-40 min-w-[200px]"
        >
          <div class="p-2 space-y-1">
            <label
              v-for="column in allColumns.filter(col => !col.required)"
              :key="column.key"
              class="flex items-center px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer"
            >
              <input
                type="checkbox"
                :checked="!hiddenColumns.has(column.key)"
                @change="toggleColumnVisibility(column.key)"
                class="rounded text-indigo-600 focus:ring-indigo-500"
              />
              <span class="ml-2 text-sm text-slate-700">{{ column.label }}</span>
            </label>
          </div>
        </div>
      </div>
    </div>

    <div class="overflow-auto max-h-[calc(100vh-400px)]" style="scrollbar-gutter: stable">
      <table class="w-full border-collapse">
        <thead>
          <tr>
            <th 
              v-for="column in visibleColumns" 
              :key="column.key" 
              class="sticky top-0 bg-slate-50 text-slate-700 font-medium px-4 py-3 text-left whitespace-nowrap z-30 shadow-[0_1px_0_0_rgba(0,0,0,0.1)]"
            >
              <div class="flex items-center space-x-1">
                <span>{{ column.label }}</span>
                <span v-if="column.sortable" 
                      class="text-slate-400 cursor-pointer"
                      @click="handleSort(column)"
                >
                  {{ sortConfig.key === column.key ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '↕️' }}
                </span>
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr 
            v-for="product in sortedProducts" 
            :key="product.id"
            :class="{
              'bg-yellow-50 hover:bg-yellow-100': product.destacado?.activo,
              'hover:bg-slate-50': !product.destacado?.activo
            }"
            class="group relative cursor-pointer"
            @click="addToCalculator(product)"
          >
            <!-- Nombre -->
            <td v-if="!hiddenColumns.has('Nombre')" class="font-medium relative px-4 py-3 border-t border-slate-100">
              <div class="flex flex-col gap-1">
                <span>{{ product.Nombre }}</span>
                <button
                  v-if="hasSimilarProducts(product)"
                  @click="(e) => handleSimilarProducts(e, product)"
                  class="text-xs text-indigo-600 hover:text-indigo-800 font-medium w-fit"
                >
                  Ver {{ product.similarProducts?.length }} productos similares
                </button>
              </div>
              <div 
                v-if="product.destacado?.activo && product.destacado.mensaje"
                class="absolute left-0 top-0 -translate-y-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10"
              >
                <div class="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-lg text-sm shadow-lg border border-yellow-200 whitespace-nowrap">
                  {{ product.destacado.mensaje }}
                  <div class="absolute left-4 top-full h-2 w-2 bg-yellow-100 transform rotate-45 translate-y-[-4px] border-b border-r border-yellow-200"></div>
                </div>
              </div>
            </td>

            <!-- Rango/Notas -->
            <td v-if="!hiddenColumns.has('Rango')" class="text-center px-4 py-3 border-t border-slate-100">
              <div class="flex flex-col items-start gap-1">
                <template v-if="configStore.getCategoryConfig(product.Categoria).hasGraduations">
                  <button
                    v-if="product.laboratories.some(lab => lab.details.graduations?.length)"
                    @click="(e) => handleShowGraduations(e, product)"
                    class="text-xs px-2 py-1 rounded-lg bg-indigo-100 text-indigo-700 hover:bg-indigo-200"
                  >
                    Ver Rango
                  </button>
                  <span v-else>-</span>
                </template>
                <template v-else-if="configStore.getCategoryConfig(product.Categoria).hasNotes">
                  <span class="text-xs text-slate-600">{{ product.Rango || '-' }}</span>
                </template>
                <span v-else>-</span>
              </div>
            </td>

            <!-- Precio Par -->
            <td v-if="!hiddenColumns.has('PrecioPar') && !configStore.getCategoryConfig(product.Categoria).onlyUnitPrice" 
                class="font-medium text-indigo-600 px-4 py-3 border-t border-slate-100">
              ${{ calculateSalePrice(product).toLocaleString() }}
            </td>

            <!-- Precio Unidad -->
            <td v-if="!hiddenColumns.has('PrecioUnidad')" class="font-medium text-indigo-600 px-4 py-3 border-t border-slate-100">
              ${{ calculateUnitPrice(product).toLocaleString() }}
            </td>

            <!-- Columnas de costo solo en modo administrador -->
            <template v-if="showActions">
              <td v-if="!hiddenColumns.has('CostoPar')" class="font-medium text-slate-600 px-4 py-3 border-t border-slate-100">
                ${{ product.laboratories[0]?.details.PrecioDeCosto.toLocaleString() }}
              </td>
              <td v-if="!hiddenColumns.has('CostoUnidad')" class="font-medium text-slate-600 px-4 py-3 border-t border-slate-100">
                ${{ calculateUnitCost(product).toLocaleString() }}
              </td>
            </template>

            <!-- Laboratorio -->
            <td v-if="!hiddenColumns.has('Laboratorio')" class="px-4 py-3 border-t border-slate-100">
              <div class="flex flex-col items-center text-center gap-1">
                <span>{{ product.laboratories[0]?.laboratory.Nombre }}</span>
                <button
                  v-if="shouldShowOtherLabs(product)"
                  @click="(e) => handleShowAllPrices(e, product)"
                  class="text-xs text-indigo-600 hover:text-indigo-800 font-medium opacity-75 hover:opacity-100 transition-opacity"
                >
                  Ver Otros Laboratorios
                </button>
              </div>
            </td>

            <!-- Tiempo de Demora -->
            <td v-if="!hiddenColumns.has('TiempoDemora')" class="px-4 py-3 border-t border-slate-100">
              {{ product.laboratories[0]?.details.TiempoDeDemora || '-' }}
            </td>

            <!-- Acciones -->
            <td v-if="showActions" class="space-x-2 px-4 py-3 border-t border-slate-100">
              <button
                @click="emit('edit', product)"
                class="text-indigo-600 hover:text-indigo-800 font-medium"
              >
                Editar
              </button>
              <button
                @click="emit('delete', product)"
                class="text-red-600 hover:text-red-800 font-medium"
              >
                Eliminar
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Modals -->
    <LaboratoryDetailsModal
      :show="showLabsModal"
      :product="selectedProduct"
      :original-product="selectedProduct ? getOriginalProduct(selectedProduct) : null"
      @close="showLabsModal = false"
    />

    <PublicRangeModal
      v-if="showGraduationsModal"
      :show="showGraduationsModal"
      :graduations="graduationsByLaboratory"
      :laboratories="laboratoriesWithGraduations"
      :product="selectedProduct!"
      @close="showGraduationsModal = false"
    />

    <SimilarProductsViewModal
      v-if="showSimilarProductsModal"
      :show="showSimilarProductsModal"
      :product="selectedProduct!"
      :all-products="productStore.products"
      @close="showSimilarProductsModal = false"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import type { ProductWithDetails } from '../../types';
import { useConfigStore, useProductStore } from '../../stores';
import PublicRangeModal from '../range-selection/PublicRangeModal.vue';
import LaboratoryDetailsModal from './LaboratoryDetailsModal.vue';
import SimilarProductsViewModal from './SimilarProductsViewModal.vue';

const props = defineProps<{
  products: ProductWithDetails[];
  originalProducts: ProductWithDetails[];
  showActions?: boolean;
}>();

const emit = defineEmits(['edit', 'delete']);

const configStore = useConfigStore();
const productStore = useProductStore();

// Estado para modales
const selectedProduct = ref<ProductWithDetails | null>(null);
const showGraduationsModal = ref(false);
const showLabsModal = ref(false);
const showSimilarProductsModal = ref(false);
const showColumnSelector = ref(false);

// Estado para ordenamiento
const sortConfig = ref<{ key: string; direction: 'asc' | 'desc' }>({
  key: 'Nombre',
  direction: 'asc'
});

// Estado para visibilidad de columnas
const hiddenColumns = ref<Set<string>>(new Set());

// Columnas de la tabla
const allColumns = computed(() => {
  const product = props.products[0];
  const config = product ? configStore.getCategoryConfig(product.Categoria) : null;
  
  const rangeColumnLabel = config?.hasGraduations ? 'Rango' : 
                          config?.hasNotes ? 'Notas' : 
                          'Rango/Notas';

  const baseColumns = [
    { key: 'Nombre', label: 'Nombre', sortable: true, required: true },
    { key: 'Rango', label: rangeColumnLabel, sortable: false, required: false }
  ];

  // Solo agregar columna de precio par si no es solo precio por unidad
  if (!config?.onlyUnitPrice) {
    baseColumns.push({ key: 'PrecioPar', label: 'Precio Par', sortable: true, required: true });
  }

  baseColumns.push({ key: 'PrecioUnidad', label: 'Precio Unidad', sortable: true, required: true });

  // Agregar columnas de costo solo en modo administrador
  if (props.showActions) {
    baseColumns.push(
      { key: 'CostoPar', label: 'Costo Par', sortable: true, required: false },
      { key: 'CostoUnidad', label: 'Costo Unidad', sortable: true, required: false }
    );
  }

  baseColumns.push(
    { key: 'Laboratorio', label: 'Laboratorio', sortable: true, required: true },
    { key: 'TiempoDemora', label: 'Tiempo de Demora', sortable: true, required: false }
  );

  if (props.showActions) {
    baseColumns.push({ key: 'actions', label: 'Acciones', sortable: false, required: true });
  }

  return baseColumns;
});

// Columnas visibles
const visibleColumns = computed(() => {
  // Si no estamos en modo administrador, mostrar todas las columnas
  if (!props.showActions) {
    return allColumns.value;
  }
  
  // En modo administrador, aplicar filtros de columnas ocultas
  return allColumns.value.filter(column => 
    column.required || !hiddenColumns.value.has(column.key)
  );
});

// Productos ordenados
const sortedProducts = computed(() => {
  return [...props.products].sort((a, b) => {
    let aValue, bValue;
    
    switch (sortConfig.value.key) {
      case 'PrecioPar':
        aValue = calculateSalePrice(a);
        bValue = calculateSalePrice(b);
        break;
      case 'PrecioUnidad':
        aValue = calculateSalePrice(a) / 2;
        bValue = calculateSalePrice(b) / 2;
        break;
      case 'CostoPar':
        aValue = a.laboratories[0]?.details.PrecioDeCosto || 0;
        bValue = b.laboratories[0]?.details.PrecioDeCosto || 0;
        break;
      case 'CostoUnidad':
        aValue = (a.laboratories[0]?.details.PrecioDeCosto || 0) / 2;
        bValue = (b.laboratories[0]?.details.PrecioDeCosto || 0) / 2;
        break;
      case 'Laboratorio':
        aValue = a.laboratories[0]?.laboratory.Nombre || '';
        bValue = b.laboratories[0]?.laboratory.Nombre || '';
        break;
      case 'TiempoDemora':
        aValue = a.laboratories[0]?.details.TiempoDeDemora || '';
        bValue = b.laboratories[0]?.details.TiempoDeDemora || '';
        break;
      default:
        aValue = (a as any)[sortConfig.value.key];
        bValue = (b as any)[sortConfig.value.key];
    }

    if (typeof aValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }

    const comparison = aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    return sortConfig.value.direction === 'asc' ? comparison : -comparison;
  });
});

// Calcular precio de venta
const calculateSalePrice = (product: ProductWithDetails) => {
  const lab = product.laboratories[0];
  if (!lab) return 0;
  return Math.round(lab.details.PrecioDeCosto * product.Factor);
};

// Calcular precio de venta por unidad
const calculateUnitPrice = (product: ProductWithDetails) => {
  const lab = product.laboratories[0];
  if (!lab) return 0;
  
  const config = configStore.getCategoryConfig(product.Categoria);
  const basePrice = lab.details.PrecioDeCosto * product.Factor;
  
  // Si la categoría está configurada para mostrar solo precio por unidad,
  // el precio base ya es por unidad
  return Math.round(config.onlyUnitPrice ? basePrice : basePrice / 2);
};

// Calcular precio de costo por unidad
const calculateUnitCost = (product: ProductWithDetails) => {
  const lab = product.laboratories[0];
  if (!lab) return 0;
  return Math.round(lab.details.PrecioDeCosto / 2);
};

// Manejar ordenamiento
const handleSort = (column: { key: string; sortable: boolean }) => {
  if (!column.sortable) return;
  
  if (sortConfig.value.key === column.key) {
    sortConfig.value.direction = sortConfig.value.direction === 'asc' ? 'desc' : 'asc';
  } else {
    sortConfig.value = {
      key: column.key,
      direction: 'asc'
    };
  }
};

// Manejar visibilidad de columnas
const toggleColumnVisibility = (columnKey: string) => {
  if (hiddenColumns.value.has(columnKey)) {
    hiddenColumns.value.delete(columnKey);
  } else {
    hiddenColumns.value.add(columnKey);
  }
};

// Obtener graduaciones por laboratorio
const graduationsByLaboratory = computed(() => {
  if (!selectedProduct.value) return {};
  
  const graduations: Record<string, Graduation[]> = {};
  selectedProduct.value.laboratories.forEach(lab => {
    if (lab.details.graduations?.length) {
      graduations[lab.laboratory.id!] = lab.details.graduations;
    }
  });
  return graduations;
});

// Obtener laboratorios con graduaciones
const laboratoriesWithGraduations = computed(() => {
  if (!selectedProduct.value) return [];
  
  return selectedProduct.value.laboratories
    .filter(lab => lab.details.graduations?.length)
    .map(lab => lab.laboratory);
});

// Obtener producto original
const getOriginalProduct = (product: ProductWithDetails) => {
  return props.originalProducts.find(p => p.id === product.id) || null;
};

// Check if we should show other laboratories button
const shouldShowOtherLabs = (product: ProductWithDetails) => {
  const originalProduct = getOriginalProduct(product);
  const config = configStore.getCategoryConfig(product.Categoria);
  return config.displayMode === 'single' && 
         originalProduct?.laboratories.length > 1;
};

// Check if product has similar products
const hasSimilarProducts = (product: ProductWithDetails) => {
  const config = configStore.getCategoryConfig(product.Categoria);
  return config.allowSimilarProducts && 
         product.similarProducts?.length > 0;
};

// Manejar clic en botones
const handleShowGraduations = (e: Event, product: ProductWithDetails) => {
  e.stopPropagation();
  if (product.laboratories.some(lab => lab.details.graduations?.length)) {
    selectedProduct.value = product;
    showGraduationsModal.value = true;
  }
};

const handleShowAllPrices = (e: Event, product: ProductWithDetails) => {
  e.stopPropagation();
  selectedProduct.value = product;
  showLabsModal.value = true;
};

const handleSimilarProducts = (e: Event, product: ProductWithDetails) => {
  e.stopPropagation();
  selectedProduct.value = product;
  showSimilarProductsModal.value = true;
};

// Cerrar menú de columnas al hacer clic fuera
const closeColumnSelector = (event: MouseEvent) => {
  const target = event.target as HTMLElement;
  if (!target.closest('.column-selector')) {
    showColumnSelector.value = false;
  }
};

// Agregar event listener para cerrar el menú
onMounted(() => {
  document.addEventListener('click', closeColumnSelector);
});

onUnmounted(() => {
  document.removeEventListener('click', closeColumnSelector);
});

// Reference to calculator component
const calculator = ref<any>(null);

// Add product to calculator
const addToCalculator = (product: ProductWithDetails) => {
  if (!calculator.value) return;
  
  const config = configStore.getCategoryConfig(product.Categoria);
  if (!config.allowCalculator) return;
  
  const unitPrice = calculateUnitPrice(product);
  calculator.value.addProduct({
    id: product.id!,
    name: product.Nombre,
    unitPrice
  });
};

defineExpose({
  calculator
});
</script>

<style scoped>
/* Hide scrollbar but keep functionality */
.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.scrollbar-hide::-webkit-scrollbar {
  display: none;
}

/* Scrollbar styles */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  @apply bg-slate-100 rounded;
}

::-webkit-scrollbar-thumb {
  @apply bg-slate-300 rounded border-2 border-slate-100;
}

::-webkit-scrollbar-thumb:hover {
  @apply bg-slate-400;
}

/* Firefox */
* {
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 #f1f5f9;
}
</style>